'use client'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useSocketContext } from '@/context/SocketProvider'
import { cn } from '@/lib/utils'
import { CheckCircle2, Mail, UserRound } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useState } from 'react'

type BonusProgressData = {
  type?: string
  label?: string
  progress_items?: BonusProgressItem[]
  progress?: {
    current?: number
    total?: number
    percentage?: number
  }
  labels?: {
    title?: string
    progress?: string
    detail?: string
  }
  values?: Record<string, string | number | undefined>
  free_spins_progress?: number
  free_spins_done?: number
  free_spins_total?: number
  money_progress?: number
  money_done?: number
  money_total?: number
}

type BonusProgressItem = {
  kind?: string
  category?: string
  label?: string
  progress?: {
    current?: number
    total?: number
    percentage?: number
  }
  labels?: {
    progress?: string
    detail?: string
  }
  values?: Record<string, string | number | undefined>
}

type CustomComponentContent = {
  type?: string
  component_id?: string
  data?: BonusProgressData & GuestInfoRequestData
}

type Translate = ReturnType<typeof useTranslations>

type GuestInfoRequestData = {
  submitted?: boolean
  submission_error?: string
  guest_info?: {
    username?: string
    email?: string
  }
}

const percent = (value?: number) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 0
  const normalized = value <= 1 ? value * 100 : value

  return Math.max(0, Math.min(100, Math.round(normalized)))
}

const numberLabel = (value?: number) => new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(value ?? 0)

const numberValue = (value: unknown) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value !== 'string' || value.trim() === '') return null
  const parsed = Number(value)

  return Number.isFinite(parsed) ? parsed : null
}

const renderTemplateValue = (value: unknown) =>
  typeof value === 'number' ? numberLabel(value) : value === undefined || value === null ? '' : String(value)

const renderLabelTemplate = (template: string, values: BonusProgressData['values'] = {}) =>
  template.replace(/\{([^}]+)\}/g, (match, rawExpression) => {
    const expression = rawExpression.trim()
    if (Object.prototype.hasOwnProperty.call(values, expression)) {
      return renderTemplateValue(values[expression])
    }

    const arithmeticMatch = expression.match(/^([A-Za-z_][A-Za-z0-9_]*)\s*([+-])\s*([A-Za-z_][A-Za-z0-9_]*)$/)
    if (!arithmeticMatch) return match

    const left = numberValue(values[arithmeticMatch[1]])
    const right = numberValue(values[arithmeticMatch[3]])
    if (left === null || right === null) return match

    return numberLabel(arithmeticMatch[2] === '-' ? left - right : left + right)
  })

const bonusTypeLabel = (type: string | undefined, t: Translate) => {
  const labels: Record<string, string> = {
    manual: t('manual'),
    deposit: t('deposit'),
    cash_back: t('cashBack'),
    bet_slip: t('betSlip'),
    registration: t('registration'),
    tournaments: t('tournaments'),
    vip_clubs: t('vipClubs'),
    free_spin: t('freeSpins')
  }

  return labels[type ?? ''] ?? (type ? type.replace(/_/g, ' ').replace(/\b\w/g, letter => letter.toUpperCase()) : t('bonusProgress'))
}

const progressRowsFromData = (data: BonusProgressData, t: Translate) => {
  if (Array.isArray(data.progress_items) && data.progress_items.length > 0) {
    return data.progress_items.map(item => ({
      category: item.category,
      label: item.labels?.progress || item.label || bonusTypeLabel(item.kind, t),
      value: item.progress ? percent(item.progress.percentage) : undefined,
      detail: item.labels?.detail
        ? renderLabelTemplate(item.labels.detail, item.values)
        : item.progress
          ? t('doneOutOf', { current: numberLabel(item.progress.current), total: numberLabel(item.progress.total) })
          : ''
    }))
  }

  if (data.progress) {
    return [{
      label: data.labels?.progress || bonusTypeLabel(data.type, t),
      value: percent(data.progress.percentage),
      detail: data.labels?.detail
        ? renderLabelTemplate(data.labels.detail, data.values)
        : t('doneOutOf', { current: numberLabel(data.progress.current), total: numberLabel(data.progress.total) })
    }]
  }

  if (data.free_spins_progress !== undefined) {
    return [{
      label: t('freeSpinsLabel'),
      value: percent(data.free_spins_progress),
      detail: t('freeSpinsDoneOutOf', { done: numberLabel(data.free_spins_done), total: numberLabel(data.free_spins_total) })
    }]
  }

  if (data.money_progress !== undefined) {
    return [{
      label: t('progress'),
      value: percent(data.money_progress),
      detail: t('wonNeedMore', {
        won: numberLabel(data.money_done),
        remaining: numberLabel(Math.max(0, (data.money_total ?? 0) - (data.money_done ?? 0)))
      })
    }]
  }

  return []
}

const ProgressRow = ({ category, label, value, detail }: { category?: string; label: string; value?: number; detail: string }) => (
  <div className='space-y-1.5'>
    {category && <div className='text-muted-foreground text-[11px] font-semibold uppercase'>{category}</div>}
    <div className='flex items-center justify-between gap-3 text-xs'>
      <span className='text-muted-foreground font-medium'>{label}</span>
      {value !== undefined && <span className='text-foreground tabular-nums'>{value}%</span>}
    </div>
    {value !== undefined && (
      <div className='bg-muted h-2 overflow-hidden rounded-full'>
        <div className='bg-primary h-full rounded-full transition-[width]' style={{ width: `${value}%` }} />
      </div>
    )}
    <div className='text-muted-foreground text-xs leading-5'>{detail}</div>
  </div>
)

const BonusProgress = ({ data, t }: { data: BonusProgressData; t: Translate }) => {
  const progressRows = progressRowsFromData(data, t)

  return (
    <div className='border-border bg-card text-card-foreground w-full min-w-0 max-w-[320px] rounded-lg border p-3 shadow-sm'>
      <div className='mb-2 text-sm font-semibold'>{data.labels?.title || data.label || bonusTypeLabel(data.type, t)}</div>
      <div className={cn('space-y-3')}>
        {progressRows.map((progress, index) => (
          <ProgressRow key={`${progress.category || progress.label}-${index}`} category={progress.category} label={progress.label} value={progress.value} detail={progress.detail} />
        ))}
      </div>
    </div>
  )
}

const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

const GuestInfoRequest = ({ data }: { data: GuestInfoRequestData }) => {
  const t = useTranslations('guestInfo')
  const { sendFrame, canSend } = useSocketContext()
  const [username, setUsername] = useState(data.guest_info?.username ?? '')
  const [email, setEmail] = useState(data.guest_info?.email ?? '')
  const [error, setError] = useState<string | null>(null)
  const submitted = data.submitted === true
  const visibleError = data.submission_error ? t('submitError') : error

  const submit = () => {
    const trimmedUsername = username.trim()
    const trimmedEmail = email.trim()

    if (!trimmedUsername || !trimmedEmail) {
      setError(t('requiredError'))

      return
    }

    if (!isValidEmail(trimmedEmail)) {
      setError(t('emailError'))

      return
    }

    if (!canSend()) {
      setError(t('connectionError'))

      return
    }

    setError(null)
    sendFrame(
      JSON.stringify({
        type: 'guest_info_submission',
        username: trimmedUsername,
        email: trimmedEmail
      })
    )
  }

  if (submitted) {
    return (
      <div className='bg-muted/50 w-full min-w-0 max-w-[320px] rounded-xl rounded-tl-none p-3'>
        <div className='flex items-center gap-2 text-sm font-semibold'>
          <CheckCircle2 className='text-primary size-4' />
          <span>{t('submittedTitle')}</span>
        </div>
        <p className='text-muted-foreground mt-1 text-xs leading-5'>{t('submittedDescription')}</p>
      </div>
    )
  }

  return (
    <div className='bg-muted/50 w-full min-w-0 max-w-[320px] rounded-xl rounded-tl-none p-3'>
      <div className='mb-3'>
        <div className='text-sm font-semibold'>{t('title')}</div>
        <p className='text-muted-foreground mt-1 text-xs leading-5'>{t('description')}</p>
      </div>
      <div className='space-y-2'>
        <label className='block space-y-1.5'>
          <span className='text-muted-foreground flex items-center gap-1.5 text-xs font-medium'>
            <UserRound className='size-3.5' />
            {t('username')}
          </span>
          <Input value={username} onChange={event => setUsername(event.target.value)} autoComplete='name' />
        </label>
        <label className='block space-y-1.5'>
          <span className='text-muted-foreground flex items-center gap-1.5 text-xs font-medium'>
            <Mail className='size-3.5' />
            {t('email')}
          </span>
          <Input value={email} onChange={event => setEmail(event.target.value)} type='email' autoComplete='email' />
        </label>
      </div>
      {visibleError && <p className='text-destructive mt-2 text-xs leading-5'>{visibleError}</p>}
      <Button type='button' className='mt-3 w-full' onClick={submit}>
        <CheckCircle2 />
        <span>{t('submit')}</span>
      </Button>
    </div>
  )
}

const parseContent = (content: unknown): CustomComponentContent | null => {
  if (content && typeof content === 'object' && (content as CustomComponentContent).type === 'component') {
    return content as CustomComponentContent
  }

  if (typeof content !== 'string') return null
  try {
    const parsed = JSON.parse(content)

    return parsed?.type === 'component' ? parsed : null
  } catch {
    return null
  }
}

export default function CustomComponentRenderer({ content }: { content: unknown }) {
  const bonusProgressT = useTranslations('bonusProgress')
  const payload = parseContent(content)
  if (!payload) return null

  if (payload.component_id === 'guest_info_request') {
    return <GuestInfoRequest data={payload.data ?? {}} />
  }

  if (payload.component_id !== 'bonus_progress') return null

  return <BonusProgress data={payload.data ?? {}} t={bonusProgressT} />
}
